// Backlinks panel — lists other notes that link TO the active note
// via [[wikilink]] syntax. Hidden when there are none. Click on a
// row opens that source note in the reader.
//
// Data comes from notes.backlinks (server walks the corpus, resolves
// wikilinks via filename → path map, returns hits with snippets).

import { useEffect, useState } from "preact/hooks";
import type { NotesBacklink } from "../../lib/rpc";

interface BacklinksProps {
  /** Path of the note whose inbound links we want. */
  activePath: string;
  /** Bump to refetch — pass the active note's mtime/sha so saves
   *  refresh the panel without us watching the whole corpus. */
  epoch: string | number;
  fetcher: (path: string) => Promise<NotesBacklink[]>;
  onPick: (path: string) => void;
}

export function NotesBacklinks({ activePath, epoch, fetcher, onPick }: BacklinksProps) {
  const [hits, setHits] = useState<NotesBacklink[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetcher(activePath).then((list) => {
      if (!cancelled) { setHits(list); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [activePath, epoch, fetcher]);

  if (loading && hits === null) return null;
  if (!hits || hits.length === 0) return null;

  return (
    <section className="notes-backlinks" aria-label="Linked from">
      <div className="notes-backlinks-head">
        Linked from <span className="notes-backlinks-count">{hits.length}</span>
      </div>
      <ul className="notes-backlinks-list">
        {hits.map((h) => (
          <li key={h.path}>
            <button type="button" className="notes-backlinks-row" onClick={() => onPick(h.path)}>
              <div className="notes-backlinks-title">{h.title}</div>
              {h.snippet && <div className="notes-backlinks-snippet">{h.snippet}</div>}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
